import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import bg from './spi.jpg';
import { collection, onSnapshot, doc, runTransaction, increment, addDoc, serverTimestamp, query, orderBy } from 'firebase/firestore';

const Home = () => {
  const [photos, setPhotos] = useState([]);
  const [selected, setSelected] = useState(null);
  const [activeImage, setActiveImage] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState({ name: '', text: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCommentForm, setShowCommentForm] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);

    // Register site view
    const registerView = async () => {
      const statsRef = doc(db, "stats", "siteMetrics");
      try {
        await runTransaction(db, async (transaction) => {
          const sfDoc = await transaction.get(statsRef);
          if (!sfDoc.exists()) {
            transaction.set(statsRef, { views: 1 });
          } else {
            transaction.update(statsRef, { views: increment(1) });
          }
        });
      } catch (e) {
        console.error(e);
      }
    };

    registerView();

    // Fetch photography collection
    const unsubPhotos = onSnapshot(collection(db, "photography"), (snap) => {
      setPhotos(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });

    return () => {
      unsubPhotos();
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Fetch comments when a photo is selected
  useEffect(() => {
    if (selected) {
      const commentsRef = collection(db, "photography", selected.id, "comments");
      const q = query(commentsRef, orderBy("timestamp", "desc"));
      
      const unsubComments = onSnapshot(q, (snap) => {
        setComments(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      });

      return () => unsubComments();
    } else {
      setComments([]);
      setNewComment({ name: '', text: '' });
      setShowCommentForm(false);
    }
  }, [selected]);

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    
    if (!newComment.name.trim() || !newComment.text.trim()) {
      alert('Please fill in both name and comment fields');
      return;
    }

    setIsSubmitting(true);
    try {
      const commentsRef = collection(db, "photography", selected.id, "comments");
      await addDoc(commentsRef, {
        name: newComment.name.trim(),
        text: newComment.text.trim(),
        timestamp: serverTimestamp()
      });
      
      setNewComment({ name: '', text: '' });
      setShowCommentForm(false);
    } catch (error) {
      console.error('Error adding comment:', error);
      alert('Failed to add comment. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Just now';
    const date = timestamp.toDate();
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const styles = {
    container: {
      backgroundImage: `url(${bg})`,
      backgroundSize: 'cover',
      backgroundPosition: isMobile ? 'right center' : 'right center',
      backgroundRepeat: 'no-repeat',
      minHeight: '100vh',
      padding: '40px',
      color: '#000',
      fontFamily: '"Arial Black", sans-serif',
      display: 'flex'
    },
    leftSection: {
      width: isMobile ? '100%' : '60%',
    },
    nav: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '40px',
      background: '#ffffff',
      padding: '15px',
      border: '6px solid #000',
      boxShadow: '10px 10px 0px #000',
    },
    navLogo: {
      fontWeight: '900',
      fontSize: '1.5rem'
    },
    navRight: {
      display: 'flex',
      gap: '15px'
    },
    navBtn: {
      border: '4px solid #000',
      padding: '8px 16px',
      fontWeight: '900',
      cursor: 'pointer',
      backgroundColor: '#3B44F6',
      color: '#fff',
      boxShadow: '6px 6px 0px #000',
      textDecoration: 'none',
      display: 'inline-block'
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
      gap: '30px',
    },
    card: {
      border: '4px solid #000',
      backgroundColor: '#fff',
      padding: '15px',
      boxShadow: '8px 8px 0px #000',
      cursor: 'pointer'
    },
    img: {
      width: '100%',
      height: '300px',
      objectFit: 'cover',
      border: '4px solid #000',
      marginBottom: '15px'
    },
    modalOverlay: {
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(255,255,255,0.95)',
      display: 'flex',
      alignItems: isMobile ? 'flex-start' : 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: isMobile ? '0' : '20px',
      overflowY: isMobile ? 'auto' : 'hidden'
    },
    modalContent: {
      backgroundColor: '#fff',
      border: '8px solid #000',
      width: isMobile ? '100%' : '90%',
      maxWidth: '1100px',
      maxHeight: isMobile ? 'none' : '90vh',
      minHeight: isMobile ? '100vh' : 'auto',
      overflowY: isMobile ? 'visible' : 'auto',
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      boxShadow: '20px 20px 0px #3B44F6',
      position: 'relative',
      margin: isMobile ? '0' : 'auto'
    },
    modalLeft: {
      flex: isMobile ? 'none' : '1.2',
      padding: '20px',
      borderRight: isMobile ? 'none' : '6px solid #000',
      borderBottom: isMobile ? '6px solid #000' : 'none',
      display: 'flex',
      flexDirection: 'column'
    },
    modalRight: {
      flex: isMobile ? 'none' : '1',
      padding: '20px',
      display: 'flex',
      flexDirection: 'column',
      overflowY: isMobile ? 'visible' : 'auto',
      minHeight: isMobile ? 'auto' : '0'
    },
    modalImage: {
      width: '100%',
      maxHeight: isMobile ? '250px' : '400px',
      objectFit: 'contain',
      border: '4px solid #000'
    },
    thumbnailRow: {
      display: 'flex',
      gap: '10px',
      marginTop: '15px',
      flexWrap: 'wrap',
      justifyContent: 'center'
    },
    thumbnail: (active) => ({
      width: isMobile ? '60px' : '70px',
      height: isMobile ? '60px' : '70px',
      objectFit: 'cover',
      border: active ? '4px solid #3B44F6' : '3px solid #000',
      cursor: 'pointer'
    }),
    closeBtn: {
      position: isMobile ? 'fixed' : 'absolute',
      top: '10px',
      right: '10px',
      background: '#FF3131',
      color: '#fff',
      border: '3px solid #000',
      padding: '5px 15px',
      fontWeight: '900',
      cursor: 'pointer',
      zIndex: 11,
      boxShadow: '4px 4px 0px #000'
    },
    commentSection: {
      marginTop: '20px',
      flex: isMobile ? 'none' : 1,
      display: 'flex',
      flexDirection: 'column'
    },
    commentHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '15px'
    },
    commentTitle: {
      fontSize: '20px',
      margin: 0
    },
    postCommentBtn: {
      border: '3px solid #000',
      padding: '8px 16px',
      fontWeight: '900',
      cursor: 'pointer',
      backgroundColor: showCommentForm ? '#FF3131' : '#3B44F6',
      color: '#fff',
      boxShadow: '4px 4px 0px #000',
      fontSize: '12px'
    },
    commentForm: {
      border: '4px solid #000',
      padding: '15px',
      backgroundColor: '#f9f9f9',
      marginBottom: '20px',
      boxShadow: '6px 6px 0px #000'
    },
    input: {
      width: '100%',
      padding: '10px',
      border: '3px solid #000',
      marginBottom: '10px',
      fontFamily: '"Arial", sans-serif',
      fontSize: '14px',
      boxSizing: 'border-box'
    },
    textarea: {
      width: '100%',
      padding: '10px',
      border: '3px solid #000',
      marginBottom: '10px',
      fontFamily: '"Arial", sans-serif',
      fontSize: '14px',
      minHeight: '80px',
      resize: 'vertical',
      boxSizing: 'border-box'
    },
    submitBtn: {
      border: '3px solid #000',
      padding: '10px 20px',
      fontWeight: '900',
      cursor: 'pointer',
      backgroundColor: '#3B44F6',
      color: '#fff',
      boxShadow: '4px 4px 0px #000',
      width: '100%',
      fontSize: '14px'
    },
    commentsList: {
      flex: isMobile ? 'none' : 1,
      overflowY: isMobile ? 'visible' : 'auto',
      maxHeight: isMobile ? 'none' : '400px'
    },
    comment: {
      border: '3px solid #000',
      padding: '12px',
      marginBottom: '15px',
      backgroundColor: '#fff',
      boxShadow: '4px 4px 0px #000'
    },
    commentHeaderRow: {
      display: 'flex',
      justifyContent: 'space-between',
      marginBottom: '8px',
      fontSize: '12px'
    },
    commentName: {
      fontWeight: '900',
      color: '#3B44F6'
    },
    commentDate: {
      color: '#666',
      fontSize: '11px'
    },
    commentText: {
      fontSize: '14px',
      lineHeight: '1.4',
      fontFamily: '"Arial", sans-serif',
      fontWeight: 'normal'
    },
    noComments: {
      textAlign: 'center',
      color: '#666',
      fontSize: '14px',
      padding: '20px'
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.leftSection}>
        {/* NAV */}
        <div style={styles.nav}>
          <div style={styles.navLogo}>ARUSH.</div>
          <div style={styles.navRight}>
            <div style={styles.navBtn} onClick={() => window.open('/donate', '_blank')}>DONATION</div>
            <div style={styles.navBtn} onClick={() => window.open('https://www.instagram.com/a._.5709/', '_blank')}>SOCIALS</div>
          </div>
        </div>

        {/* GRID */}
        <div style={styles.grid}>
          {photos.map(photo => (
            <div
              key={photo.id}
              style={styles.card}
              onClick={() => {
                setSelected(photo);
                setActiveImage(0);
              }}
            >
              <img
                src={photo.images?.[0] || photo.image}
                alt={photo.title}
                style={styles.img}
              />
              <h3>{photo.title}</h3>
              <p>{photo.date}</p>
              <p>click to see more</p>
            </div>
          ))}
        </div>

        {/* MODAL */}
        {selected && (
          <div style={styles.modalOverlay} onClick={() => setSelected(null)}>
            <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
              <button
                onClick={() => setSelected(null)}
                style={styles.closeBtn}
              >
                X
              </button>

              {/* LEFT: Image and Info */}
              <div style={styles.modalLeft}>
                <img
                  src={selected.images?.[activeImage] || selected.images?.[0] || selected.image}
                  alt={selected.title}
                  style={styles.modalImage}
                />

                {selected.images && selected.images.length > 1 && (
                  <div style={styles.thumbnailRow}>
                    {selected.images.map((img, index) => (
                      <img
                        key={index}
                        src={img}
                        alt={`${selected.title} ${index + 1}`}
                        onClick={() => setActiveImage(index)}
                        style={styles.thumbnail(activeImage === index)}
                      />
                    ))}
                  </div>
                )}

                <div style={{ marginTop: '20px' }}>
                  <h2>{selected.title}</h2>
                  <p style={{ fontFamily: '"Arial", sans-serif', fontWeight: 'normal' }}>
                    {selected.content}
                  </p>
                  <p style={{ marginTop: '10px', fontSize: '14px' }}>
                    <strong>LOC:</strong> {selected.category}<br />
                    <strong>DATE:</strong> {selected.date}
                  </p>
                </div>
              </div>

              {/* RIGHT: Comments */}
              <div style={styles.modalRight}>
                <div style={styles.commentHeader}>
                  <h3 style={styles.commentTitle}>
                    COMMENTS ({comments.length})
                  </h3>
                  <button
                    onClick={() => setShowCommentForm(!showCommentForm)}
                    style={{...styles.postCommentBtn,marginTop:50}}
                  >
                    {showCommentForm ? 'CANCEL' : 'POST COMMENT'}
                  </button>
                </div>

                {/* Comment Form (conditionally rendered) */}
                {showCommentForm && (
                  <form onSubmit={handleSubmitComment} style={styles.commentForm}>
                    <input
                      type="text"
                      placeholder="Your name"
                      value={newComment.name}
                      onChange={(e) => setNewComment({ ...newComment, name: e.target.value })}
                      style={styles.input}
                      maxLength={50}
                    />
                    <textarea
                      placeholder="Write a comment..."
                      value={newComment.text}
                      onChange={(e) => setNewComment({ ...newComment, text: e.target.value })}
                      style={styles.textarea}
                      maxLength={500}
                    />
                    <button
                      type="submit"
                      style={styles.submitBtn}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'POSTING...' : 'SUBMIT COMMENT'}
                    </button>
                  </form>
                )}

                {/* Comments List */}
                <div style={styles.commentsList}>
                  {comments.length === 0 ? (
                    <div style={styles.noComments}>
                      No comments yet. Be the first to comment!
                    </div>
                  ) : (
                    comments.map(comment => (
                      <div key={comment.id} style={styles.comment}>
                        <div style={styles.commentHeaderRow}>
                          <span style={styles.commentName}>{comment.name}</span>
                          <span style={styles.commentDate}>
                            {formatDate(comment.timestamp)}
                          </span>
                        </div>
                        <p style={styles.commentText}>{comment.text}</p>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Home;